import React from "react";
import { Delta, InfoButton, Sparkline } from "./StatsPrimitives";

// Icons of the KPI tiles (static markup, decorative), shared by the statistics screens.
const ICON_PROPS = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
export const KPI_ICONS = {
  occ: (
    <svg {...ICON_PROPS}>
      <path d="M12 3a9 9 0 1 0 9 9h-9z" />
      <path d="M15 3.5A9 9 0 0 1 20.5 9H15z" />
    </svg>
  ),
  rev: (
    <svg {...ICON_PROPS}>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M6 9.5v5M18 9.5v5" />
    </svg>
  ),
  res: (
    <svg {...ICON_PROPS}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
      <path d="m9 15 2 2 4-4" />
    </svg>
  ),
  iph: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  ),
  ticket: (
    <svg {...ICON_PROPS}>
      <path d="M4 7h16v4a2 2 0 0 0 0 4v4H4v-4a2 2 0 0 0 0-4z" />
      <path d="M10 9.5h4M10 14.5h4" />
    </svg>
  ),
  comm: (
    <svg {...ICON_PROPS}>
      <path d="M19 5 5 19" />
      <circle cx="7" cy="7" r="2.5" />
      <circle cx="17" cy="17" r="2.5" />
    </svg>
  ),
};

/**
 * Headline tile: icon, label with its definition popover, big value, change vs the comparison period
 * and a sparkline.
 *
 * @param {{icon: keyof typeof KPI_ICONS, definition: [string, string], label: string, value: string,
 *   delta: object|null, compareLabel: string, sub: string, spark: number[]}} props
 */
export function KpiTile({ icon, definition, label, value, delta, compareLabel, sub, spark }) {
  return (
    <article className="stats-kpi">
      <div className="stats-kpi__top">
        <span className="stats-kpi__icon" aria-hidden="true">
          {KPI_ICONS[icon]}
        </span>
        <p className="stats-kpi__label">{label}</p>
        <InfoButton definition={definition} label={label} />
      </div>
      <p className="stats-kpi__value">{value}</p>
      {delta && (
        <div className="stats-kpi__foot">
          <Delta delta={delta} />
          <span>{compareLabel}</span>
        </div>
      )}
      <p className="stats-kpi__sub">{sub}</p>
      <Sparkline values={spark} />
    </article>
  );
}

/** Smaller metric of the rates strip: label with definition popover, value, change and a note. */
export function Stat({ definition, label, value, delta, compareLabel, sub }) {
  return (
    <div className="stats-stat">
      <div className="stats-stat__top">
        <p className="stats-stat__label">{label}</p>
        <InfoButton definition={definition} label={label} />
      </div>
      <p className="stats-stat__value">{value}</p>
      {delta && (
        <div className="stats-stat__foot">
          <Delta delta={delta} />
          <span>{compareLabel}</span>
        </div>
      )}
      <p className="stats-stat__sub">{sub}</p>
    </div>
  );
}

/** Insight text: odd `parts` are bold, so no HTML strings are needed. */
const InsightText = ({ parts }) => (
  <p className="stats-insight__text">
    {parts.map((part, index) => (index % 2 ? <strong key={index}>{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>))}
  </p>
);

/** Up to three "what the numbers say" cards (`{key, eyebrow, tone, parts}` as built by the insight selectors). */
export function InsightCards({ insights }) {
  if (insights.length === 0) return null;
  return (
    <section className="stats-insights" aria-label="Lo que dicen los números">
      {insights.map((card) => (
        <article key={card.key} className="stats-insight">
          <p className="stats-insight__eyebrow">
            <i className={`stats-insight__dot ${card.tone}`} aria-hidden="true" />
            {card.eyebrow}
          </p>
          <InsightText parts={card.parts} />
        </article>
      ))}
    </section>
  );
}
